import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { type Filter, type Event } from 'nostr-tools';
import { getReadRelays, getLocalEvents } from './useNostrPublish';
import { getMaxRelays } from '../lib/platform';

// ══════════════════════════════════════════════════════════════════
//  LOCAL EVENT MATCHING — merge locally published events into queries
// ══════════════════════════════════════════════════════════════════

/** Check if an event matches a single Nostr filter */
function eventMatchesFilter(event: Event, filter: Filter): boolean {
  if (filter.ids && !filter.ids.includes(event.id)) return false;
  if (filter.kinds && !filter.kinds.includes(event.kind)) return false;
  if (filter.authors && !filter.authors.includes(event.pubkey)) return false;
  if (filter.since && event.created_at < filter.since) return false;
  if (filter.until && event.created_at > filter.until) return false;

  // Check tag filters (#t, #e, #p, #d, #a, etc.)
  for (const key of Object.keys(filter)) {
    if (key.startsWith('#') && key.length === 2) {
      const tagName = key[1];
      const filterValues = (filter as any)[key] as string[];
      if (filterValues && filterValues.length > 0) {
        const eventTagValues = event.tags
          .filter((t: string[]) => t[0] === tagName)
          .map((t: string[]) => t[1]);
        if (!filterValues.some(v => eventTagValues.includes(v))) return false;
      }
    }
  }

  return true;
}

/** Get local events matching any of the given filters */
function getMatchingLocalEvents(filters: Filter[]): Event[] {
  const localEvents = getLocalEvents();
  if (localEvents.length === 0 || filters.length === 0) return [];

  return localEvents.filter(event =>
    filters.some(filter => eventMatchesFilter(event, filter))
  );
}

// ══════════════════════════════════════════════════════════════════
//  REPLACEABLE EVENT DEDUPLICATION
//
//  For replaceable (kind 0, 3, 10000-19999) and parameterized
//  replaceable (kind 30000-39999) events, only the NEWEST event per
//  author+kind (+ d-tag for param-replaceable) should be kept.
//  Without this, multiple versions of the same contact list (kind 3)
//  from different relays cause follow counts to jump around.
// ══════════════════════════════════════════════════════════════════

function isReplaceableKind(kind: number): boolean {
  return kind === 0 || kind === 3 || (kind >= 10000 && kind < 20000);
}

function isParamReplaceableKind(kind: number): boolean {
  return kind >= 30000 && kind < 40000;
}

/**
 * Deduplicate events, keeping only the newest version of replaceable events.
 * For regular events: deduplicate by event ID.
 * For replaceable events: keep newest per author+kind.
 * For param-replaceable events: keep newest per author+kind+d-tag.
 */
function deduplicateEvents(events: Event[]): Event[] {
  // First pass: for replaceable events, find the newest per key
  const replaceableNewest = new Map<string, Event>(); // key → newest event
  const replaceableEventIds = new Set<string>(); // all event IDs that are replaceable

  for (const ev of events) {
    if (isReplaceableKind(ev.kind)) {
      const key = `${ev.kind}:${ev.pubkey}`;
      replaceableEventIds.add(ev.id);
      const existing = replaceableNewest.get(key);
      if (!existing || ev.created_at > existing.created_at ||
          (ev.created_at === existing.created_at && ev.id > existing.id)) {
        replaceableNewest.set(key, ev);
      }
    } else if (isParamReplaceableKind(ev.kind)) {
      const dTag = ev.tags.find((t: string[]) => t[0] === 'd')?.[1] || '';
      const key = `${ev.kind}:${ev.pubkey}:${dTag}`;
      replaceableEventIds.add(ev.id);
      const existing = replaceableNewest.get(key);
      if (!existing || ev.created_at > existing.created_at ||
          (ev.created_at === existing.created_at && ev.id > existing.id)) {
        replaceableNewest.set(key, ev);
      }
    }
  }

  // Build set of winning replaceable event IDs
  const winnerIds = new Set<string>();
  for (const ev of replaceableNewest.values()) {
    winnerIds.add(ev.id);
  }

  // Second pass: filter — keep non-replaceable (dedupe by ID) + winning replaceable
  const seenIds = new Set<string>();
  return events.filter(ev => {
    if (seenIds.has(ev.id)) return false;
    seenIds.add(ev.id);
    // If it's a replaceable event, only keep the winner
    if (replaceableEventIds.has(ev.id)) {
      return winnerIds.has(ev.id);
    }
    return true;
  });
}

// ══════════════════════════════════════════════════════════════════
//  RELAY POOL — shared connections, O(1) routing, auto-reconnect
// ══════════════════════════════════════════════════════════════════

const pool: Map<string, WebSocket> = new Map();
const subHandlers: Map<string, (data: any) => void> = new Map();
const pendingReqs: Map<string, { filters: Filter[]; subId: string }[]> = new Map();
const relayFailCount: Map<string, number> = new Map();
const MAX_RELAY_FAILURES = 5;
let _nextId = 1;
let _preconnected = false;

function routeMessage(_url: string, raw: string) {
  try {
    const data = JSON.parse(raw);
    if (!Array.isArray(data) || data.length < 2) return;
    // Sanitize EVENT payloads — ensure tags is always an array.
    // Malformed relay events with missing/null tags would crash every
    // .tags.find() / .tags.filter() call downstream.
    if (data[0] === 'EVENT' && data[2]) {
      const ev = data[2];
      if (!ev.id || typeof ev.id !== 'string') return; // Skip broken events
      if (!Array.isArray(ev.tags)) ev.tags = [];
      if (typeof ev.content !== 'string') ev.content = '';
    }
    subHandlers.get(data[1])?.(data);
  } catch {}
}

function connectRelay(url: string): WebSocket | null {
  const existing = pool.get(url);
  if (existing && existing.readyState <= 1) return existing;
  // Skip relays that have failed too many times
  const failures = relayFailCount.get(url) || 0;
  if (failures >= MAX_RELAY_FAILURES) return null;
  try {
    const ws = new WebSocket(url);
    pool.set(url, ws);
    ws.onmessage = (e) => routeMessage(url, e.data);
    ws.onopen = () => {
      relayFailCount.set(url, 0); // Reset on successful connection
      const pending = pendingReqs.get(url);
      if (pending) {
        pending.forEach(({ filters, subId }) => {
          try { ws.send(JSON.stringify(['REQ', subId, ...filters])); } catch {}
        });
        pendingReqs.delete(url);
      }
    };
    ws.onclose = () => {
      pool.delete(url);
      const fails = (relayFailCount.get(url) || 0) + 1;
      relayFailCount.set(url, fails);
      if (fails < MAX_RELAY_FAILURES) {
        const delay = Math.min(3000 * Math.pow(2, fails - 1), 60000); // Exponential backoff, max 60s
        setTimeout(() => { if (!pool.has(url)) connectRelay(url); }, delay);
      }
    };
    ws.onerror = () => {};
    return ws;
  } catch { return null; }
}

export function preconnectRelays() {
  if (_preconnected) return;
  _preconnected = true;
  const max = getMaxRelays();
  getReadRelays().slice(0, Math.min(3, max)).forEach(connectRelay);
}

/**
 * Force-reconnect the relay pool.
 *
 * iOS PWAs (and mobile browsers backgrounded for a while) get fully
 * suspended: the OS silently kills all WebSocket connections while the app
 * is off-screen. Unlike a fresh page load, resuming the app does NOT
 * re-open those sockets — the pool still holds dead/closing handles, so the
 * next query sends REQs into the void and nothing ever comes back (the
 * telltale `Closed … 1005` + subscription `Timeout` pattern).
 *
 * Call this when the app returns to the foreground. It drops any socket that
 * isn't cleanly OPEN, clears the failure backoff so previously-timed-out
 * relays get another chance, and re-establishes the base connections so
 * subsequent refetches actually reach a live relay.
 */
export function reconnectRelays() {
  // Clear the exponential-backoff counters — a suspended app that comes back
  // to network should not be punished for sockets the OS killed.
  relayFailCount.clear();

  // Drop every socket that isn't cleanly OPEN. Closing them here also cancels
  // any pending reconnect timers implicitly, since onclose re-checks the pool.
  pool.forEach((ws, url) => {
    if (ws.readyState !== WebSocket.OPEN) {
      try { ws.close(); } catch { /* ignore */ }
      pool.delete(url);
    }
  });

  // Re-open the base relay set so the next query has live connections.
  const max = getMaxRelays();
  getReadRelays().slice(0, Math.min(3, max)).forEach(connectRelay);
}

function sendReq(url: string, subId: string, filters: Filter[]) {
  const ws = connectRelay(url);
  if (!ws) return;
  if (ws.readyState === WebSocket.OPEN) {
    try { ws.send(JSON.stringify(['REQ', subId, ...filters])); } catch {}
  } else {
    if (!pendingReqs.has(url)) pendingReqs.set(url, []);
    pendingReqs.get(url)!.push({ filters, subId });
  }
}

function closeSub(subId: string) {
  subHandlers.delete(subId);
  pool.forEach(ws => {
    if (ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(['CLOSE', subId])); } catch {}
    }
  });
}

// ══════════════════════════════════════════════════════════════════
//  QUERY CACHE — instant navigations
// ══════════════════════════════════════════════════════════════════

const queryCache: Map<string, Event[]> = new Map();
const cacheTimes: Map<string, number> = new Map();
// 5 minutes — must survive typical back/forward navigation round-trips.
// Previous 60s was too short: user scrolls feed → clicks post → reads it
// for 60s+ → hits back → cache expired → empty feed → scroll restoration fails.
const CACHE_TTL = 5 * 60 * 1000;

function getCached(key: string): Event[] | null {
  const time = cacheTimes.get(key);
  if (!time || Date.now() - time > CACHE_TTL) return null;
  return queryCache.get(key) || null;
}
function setCache(key: string, events: Event[]) {
  queryCache.set(key, events);
  cacheTimes.set(key, Date.now());
}

// ══════════════════════════════════════════════════════════════════
//  useNostrQuery — zero-flicker, cache-first
// ══════════════════════════════════════════════════════════════════

interface UseNostrQueryOptions {
  filters: Filter[];
  enabled?: boolean;
  dedupe?: boolean;
  allRelays?: boolean;
  /** Additional relay URLs to query alongside the user's configured relays.
   *  Useful for specialized relays (e.g. video-optimized relays for clips). */
  extraRelays?: string[];
}

export function useNostrQuery({ filters, enabled = true, dedupe = true, allRelays = false, extraRelays }: UseNostrQueryOptions) {
  // Stable filter key — only recompute when filters actually change
  const filtersJson = JSON.stringify(filters);
  const filterKey = useMemo(() => {
    if (!enabled || filters.length === 0) return '';
    return filtersJson;
  }, [enabled, filtersJson]);

  // A stable key for the extra relays. The subscription must re-open when
  // these arrive late (e.g. a profile's NIP-65 write relays resolve after the
  // first query), so it's part of the subscription effect's deps below. It is
  // deliberately NOT part of filterKey (the cache key): the same filter yields
  // the same events regardless of which relays answered, so results from the
  // base relays and the extra relays should share one cache entry.
  const extraRelaysKey = useMemo(
    () => (extraRelays && extraRelays.length ? [...extraRelays].sort().join(',') : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extraRelays?.join(',')],
  );

  const cached = filterKey ? getCached(filterKey) : null;
  // Seed with local events matching the filters so own posts appear instantly
  const initialLocalEvents = useMemo(() => {
    if (!filterKey || filters.length === 0) return [];
    return getMatchingLocalEvents(filters);
  }, [filterKey]);
  const [events, setEvents] = useState<Event[]>(() => {
    // Merge cache + local events so own posts always appear
    const base = cached || [];
    const local = initialLocalEvents;
    if (local.length === 0) return base;
    const merged = [...base, ...local];
    merged.sort((a, b) => b.created_at - a.created_at);
    return deduplicateEvents(merged);
  });
  const [loading, setLoading] = useState(!cached && initialLocalEvents.length === 0 && !!filterKey);

  const seenIds = useRef(new Set<string>());
  const pendingBatch = useRef<Event[]>([]);
  const batchTimer = useRef<any>(null);
  const subIdRef = useRef('');
  const doneRef = useRef(!!cached);
  const mountedRef = useRef(true);
  const prevFilterKeyRef = useRef(filterKey);
  const prevExtraRelaysKeyRef = useRef(extraRelaysKey);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const flush = useCallback(() => {
    if (!mountedRef.current || pendingBatch.current.length === 0) return;
    const batch = pendingBatch.current;
    pendingBatch.current = [];

    setEvents(prev => {
      const all = [...prev, ...batch];
      all.sort((a, b) => b.created_at - a.created_at);
      if (!dedupe) { if (filterKey) setCache(filterKey, all); return all; }
      // Deduplicate by event ID + replaceable event semantics (newest per author+kind)
      const result = deduplicateEvents(all);
      if (filterKey) setCache(filterKey, result);
      return result;
    });
  }, [dedupe, filterKey]);

  // Main subscription effect
  useEffect(() => {
    preconnectRelays();

    // Clean up previous
    if (subIdRef.current) closeSub(subIdRef.current);
    if (batchTimer.current) { clearTimeout(batchTimer.current); batchTimer.current = null; }

    if (!filterKey) {
      if (events.length > 0) setEvents([]);
      setLoading(false);
      prevFilterKeyRef.current = filterKey;
      return;
    }

    // Only reset events when the filter ACTUALLY CHANGED to a different query
    // This prevents flickering when the same query re-runs
    const filterChanged = prevFilterKeyRef.current !== filterKey;
    prevFilterKeyRef.current = filterKey;

    // Did the extra relays change since the last run? When they do (e.g. a
    // profile's NIP-65 write relays resolved after the first query), we must
    // actually open a subscription to those new relays — so we bypass the
    // cache-only fast path below, which would otherwise short-circuit and
    // never contact the newly-known relays, leaving the profile blank.
    const extraRelaysChanged = prevExtraRelaysKeyRef.current !== extraRelaysKey;
    prevExtraRelaysKeyRef.current = extraRelaysKey;

    // ── Cache-only fast path ────────────────────────────────────
    // If the cache is fresh, serve from cache and SKIP the relay
    // subscription entirely. This prevents the thundering herd of
    // CLOSE+REQ messages when navigating between pages (e.g. clicking
    // Home closes all WebSocket subs and reconnects to ALL relays).
    // The data is already in memory — no need to re-query relays.
    const freshCache = getCached(filterKey);
    if (freshCache && freshCache.length > 0 && !extraRelaysChanged) {
      // Always merge local events into cache — even when filter hasn't changed.
      // This is critical for features like boost: the boost event is stored
      // locally via useNostrPublish, but if the cache-fast-path skips merging,
      // the boost won't appear in the feed until the cache expires (5 min).
      const localMatches = getMatchingLocalEvents(filters);
      const fresh = [...freshCache, ...localMatches];
      fresh.sort((a, b) => b.created_at - a.created_at);
      // Deduplicate by ID + replaceable event semantics (newest per author+kind)
      const deduped = deduplicateEvents(fresh);
      seenIds.current.clear();
      // Only update state if the events actually changed (avoid infinite re-render loops)
      if (filterChanged || deduped.length !== events.length || deduped.some((e, i) => e.id !== events[i]?.id)) {
        setEvents(deduped);
      }
      deduped.forEach((e: Event) => seenIds.current.add(e.id));
      setLoading(false);
      doneRef.current = true;
      // No relay subscription opened — no cleanup needed
      subIdRef.current = '';
      return;
    }

    if (filterChanged) {
      // Different query without fresh cache — need relay data
      const localMatches = getMatchingLocalEvents(filters);
      seenIds.current.clear();
      if (localMatches.length > 0) {
        localMatches.sort((a, b) => b.created_at - a.created_at);
        const deduped = deduplicateEvents(localMatches);
        setEvents(deduped);
        deduped.forEach((e: Event) => seenIds.current.add(e.id));
      } else {
        setEvents([]);
      }
      // Show loading spinner since we have no cache
      setLoading(true);
    } else {
      // Same query re-running with no cache — refresh from relays
      // Pre-seed seen IDs from current events to avoid duplicates
      seenIds.current.clear();
      events.forEach(e => seenIds.current.add(e.id));
    }

    pendingBatch.current = [];
    doneRef.current = false;

    const maxRelays = getMaxRelays();
    const baseRelays = (allRelays ? getReadRelays() : getReadRelays().slice(0, 3)).slice(0, maxRelays);
    // Merge extra relays (deduped) — these are queried alongside the user's relays
    const relays = extraRelays?.length
      // extraRelays first so they survive the maxRelays cap (Data Saver mode)
      ? [...new Set([...extraRelays, ...baseRelays])].slice(0, maxRelays)
      : baseRelays;
    const subId = 'q' + (_nextId++);
    subIdRef.current = subId;
    let eoseReceived = false;

    subHandlers.set(subId, (data: any) => {
      if (!mountedRef.current) return;
      if (data[0] === 'EVENT' && data[1] === subId) {
        const event = data[2] as Event;
        if (dedupe && seenIds.current.has(event.id)) return;
        seenIds.current.add(event.id);
        pendingBatch.current.push(event);
        if (!batchTimer.current) {
          batchTimer.current = setTimeout(() => { batchTimer.current = null; flush(); }, 50);
        }
      } else if (data[0] === 'EOSE' && data[1] === subId) {
        if (!eoseReceived) {
          eoseReceived = true;
          doneRef.current = true;
          if (batchTimer.current) { clearTimeout(batchTimer.current); batchTimer.current = null; }
          flush();
          setLoading(false);
        }
      }
    });

    relays.forEach(url => sendReq(url, subId, filters));

    // Safety timeout — only fires when NO relay sends EOSE (all slow/dead).
    // Loading normally clears on the first relay's EOSE (see handler above),
    // and events keep streaming in via flush() even after this fires, so a
    // shorter timeout just stops the spinner sooner in the degraded case
    // without dropping any late-arriving events. 5s felt like a hang; 2.5s
    // is still generous for a healthy relay to answer.
    const safetyTimer = setTimeout(() => {
      if (!doneRef.current && mountedRef.current) {
        doneRef.current = true;
        flush();
        setLoading(false);
      }
    }, 2500);

    return () => {
      closeSub(subId);
      clearTimeout(safetyTimer);
      if (batchTimer.current) { clearTimeout(batchTimer.current); batchTimer.current = null; }
    };
    // extraRelaysKey is included so the subscription re-opens when extra relays
    // arrive late (e.g. a profile's write relays), without changing filterKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, extraRelaysKey]); // Re-run when the filter OR the extra relays change

  const refetch = useCallback(() => {
    // Force clear cache for this key and re-trigger
    if (filterKey) {
      queryCache.delete(filterKey);
      cacheTimes.delete(filterKey);
    }
    // Keep existing events visible while refetching (no flash of empty)
    // Seed seen IDs from current events + local events
    seenIds.current.clear();
    pendingBatch.current = [];
    doneRef.current = false;

    const localMatches = getMatchingLocalEvents(filters);
    // Merge current events with local matches so nothing disappears
    setEvents(prev => {
      const merged = [...prev, ...localMatches];
      // Deduplicate by ID + replaceable event semantics (newest per author+kind)
      const deduped = deduplicateEvents(merged);
      deduped.forEach(e => seenIds.current.add(e.id));
      return deduped;
    });
    setLoading(true);

    if (!filterKey) return;

    if (subIdRef.current) closeSub(subIdRef.current);
    const maxRelays = getMaxRelays();
    const baseRelays = (allRelays ? getReadRelays() : getReadRelays().slice(0, 3)).slice(0, maxRelays);
    const relays = extraRelays?.length
      // extraRelays first so they survive the maxRelays cap (Data Saver mode)
      ? [...new Set([...extraRelays, ...baseRelays])].slice(0, maxRelays)
      : baseRelays;
    const subId = 'q' + (_nextId++);
    subIdRef.current = subId;
    let eoseReceived = false;

    subHandlers.set(subId, (data: any) => {
      if (!mountedRef.current) return;
      if (data[0] === 'EVENT' && data[1] === subId) {
        const event = data[2] as Event;
        if (dedupe && seenIds.current.has(event.id)) return;
        seenIds.current.add(event.id);
        pendingBatch.current.push(event);
        if (!batchTimer.current) {
          batchTimer.current = setTimeout(() => { batchTimer.current = null; flush(); }, 50);
        }
      } else if (data[0] === 'EOSE' && data[1] === subId) {
        if (!eoseReceived) {
          eoseReceived = true;
          if (batchTimer.current) { clearTimeout(batchTimer.current); batchTimer.current = null; }
          flush();
          setLoading(false);
        }
      }
    });

    relays.forEach(url => sendReq(url, subId, filters));
  }, [filterKey, dedupe, flush, filters]);

  return { events, loading, refetch };
}

// ══════════════════════════════════════════════════════════════════
//  PROFILE BATCHING — single REQ for multiple pubkeys
//  + localStorage persistence for instant load on revisit
// ══════════════════════════════════════════════════════════════════

const PROFILE_STORE_KEY = 'bchnostr:profiles';
const PROFILE_STALE_MS = 60 * 60 * 1000; // 1 hour — re-fetch in background after this
const PROFILE_MAX_STORED = 500; // Keep top 500 most-recently-seen profiles

// ── Persist helpers ───────────────────────────────────────────────

function loadStoredProfiles(): Map<string, any> {
  try {
    const raw = localStorage.getItem(PROFILE_STORE_KEY);
    if (!raw) return new Map();
    const obj = JSON.parse(raw) as Record<string, any>;
    return new Map(Object.entries(obj));
  } catch {
    return new Map();
  }
}

function saveProfileToStore(pubkey: string, profile: any) {
  try {
    const raw = localStorage.getItem(PROFILE_STORE_KEY);
    const obj: Record<string, any> = raw ? JSON.parse(raw) : {};
    obj[pubkey] = profile;

    // Prune to MAX_STORED entries — remove oldest by _savedAt
    const entries = Object.entries(obj);
    if (entries.length > PROFILE_MAX_STORED) {
      entries.sort((a, b) => (b[1]?._savedAt || 0) - (a[1]?._savedAt || 0));
      const pruned = Object.fromEntries(entries.slice(0, PROFILE_MAX_STORED));
      localStorage.setItem(PROFILE_STORE_KEY, JSON.stringify(pruned));
    } else {
      localStorage.setItem(PROFILE_STORE_KEY, JSON.stringify(obj));
    }
  } catch {}
}

// Identity fields that are expensive to lose: a newer kind 0 that blanks
// or omits them should not erase a previously-known good value, because
// some clients re-publish partial profiles. `next` is mutated in place.
const PROFILE_STICKY_FIELDS = ['name', 'display_name', 'nip05', 'picture'] as const;

function mergeProfileIdentityFields(next: any, prev: any): void {
  if (!prev || typeof prev !== 'object') return;
  for (const field of PROFILE_STICKY_FIELDS) {
    const fresh = next[field];
    const isBlank = fresh === undefined || fresh === null
      || (typeof fresh === 'string' && fresh.trim() === '');
    const prevVal = prev[field];
    const prevUsable = typeof prevVal === 'string' && prevVal.trim() !== '';
    if (isBlank && prevUsable) next[field] = prevVal;
  }
}

// ── Seed the in-memory cache from localStorage immediately ────────
// This runs at module load time so profiles are available before
// any React component renders — no relay round-trip needed.
const profileCache: Map<string, any> = loadStoredProfiles();

const profilePending: Set<string> = new Set();
const profileCallbacks: Map<string, Set<(p: any) => void>> = new Map();
let profileBatchTimer: any = null;
const profileBatchQueue: string[] = [];

function flushProfileBatch() {
  if (profileBatchQueue.length === 0) return;
  const pubkeys = [...new Set(profileBatchQueue)];
  profileBatchQueue.length = 0;

  const subId = 'p' + (_nextId++);
  const relays = getReadRelays().slice(0, 3);

  subHandlers.set(subId, (data: any) => {
    if (data[0] === 'EVENT' && data[1] === subId) {
      try {
        const event = data[2];
        // Only update if newer than what we have
        const existing = profileCache.get(event.pubkey);
        if (existing && existing._ts && existing._ts > event.created_at) return;

        const parsed = JSON.parse(event.content);
        parsed._ts = event.created_at;
        parsed._savedAt = Date.now();
        // Some clients re-publish a kind 0 that blanks fields they don't
        // manage (e.g. name/display_name/nip05 come back as ""). The event
        // is newer, so it legitimately wins — but a newer profile that
        // simply DROPPED a field shouldn't erase what we already knew. For
        // display-critical fields, keep the last non-empty value when the
        // fresh event leaves it blank. Explicitly-set values always win.
        mergeProfileIdentityFields(parsed, existing);
        profileCache.set(event.pubkey, parsed);
        // Persist to localStorage so next page load is instant
        saveProfileToStore(event.pubkey, parsed);

        profilePending.delete(event.pubkey);
        profileCallbacks.get(event.pubkey)?.forEach(cb => cb(parsed));
        profileCallbacks.delete(event.pubkey);
      } catch {}
    } else if (data[0] === 'EOSE' && data[1] === subId) {
      // For any pubkey we didn't receive a profile for, mark as null
      // so we don't keep re-requesting on every render
      pubkeys.forEach(pk => {
        if (!profileCache.has(pk)) {
          profileCache.set(pk, null);
        }
        profilePending.delete(pk);
        profileCallbacks.get(pk)?.forEach(cb => cb(profileCache.get(pk) ?? null));
        profileCallbacks.delete(pk);
      });
      setTimeout(() => closeSub(subId), 2000);
    }
  });

  relays.forEach(url => sendReq(url, subId, [{ kinds: [0], authors: pubkeys, limit: pubkeys.length * 2 }]));
}

function requestProfile(pubkey: string, callback: (p: any) => void) {
  const cached = profileCache.get(pubkey);

  if (cached !== undefined) {
    // Serve from cache immediately
    callback(cached);

    // Stale-while-revalidate: if cached but old, re-fetch silently in background
    const savedAt = cached?._savedAt || 0;
    const isStale = Date.now() - savedAt > PROFILE_STALE_MS;
    if (isStale && cached !== null && !profilePending.has(pubkey)) {
      // Register callback so the component gets the fresh data when it arrives
      if (!profileCallbacks.has(pubkey)) profileCallbacks.set(pubkey, new Set());
      profileCallbacks.get(pubkey)!.add(callback);

      profilePending.add(pubkey);
      profileBatchQueue.push(pubkey);
      if (!profileBatchTimer) {
        profileBatchTimer = setTimeout(() => { profileBatchTimer = null; flushProfileBatch(); }, 10);
      }
    }
    return;
  }

  // Not in cache — add to batch
  if (!profileCallbacks.has(pubkey)) profileCallbacks.set(pubkey, new Set());
  profileCallbacks.get(pubkey)!.add(callback);

  if (profilePending.has(pubkey)) return;

  profilePending.add(pubkey);
  profileBatchQueue.push(pubkey);
  if (!profileBatchTimer) {
    // Fire quickly — 10ms debounce collects burst of simultaneous requests
    profileBatchTimer = setTimeout(() => { profileBatchTimer = null; flushProfileBatch(); }, 10);
  }
}

/** Invalidate a cached profile so it gets re-fetched */
export function invalidateProfileCache(pubkey: string) {
  profileCache.delete(pubkey);
  profilePending.delete(pubkey);
  // Also clear from localStorage
  try {
    const raw = localStorage.getItem(PROFILE_STORE_KEY);
    if (raw) {
      const obj = JSON.parse(raw);
      delete obj[pubkey];
      localStorage.setItem(PROFILE_STORE_KEY, JSON.stringify(obj));
    }
  } catch {}
}

/**
 * Fetch a user's profile (kind 0) from relays — returns a Promise.
 * Uses the same internal cache + batch system. Safe to call multiple times.
 */
/** Synchronous profile lookup from cache. Returns null if not cached yet. */
export function getCachedProfile(pubkey: string): any | null {
  return profileCache.get(pubkey) ?? null;
}

/** Search the profile cache by display_name, name, or nip05 username.
 *  Returns { pubkey, profile } or null if not found.
 *  Case-insensitive. Prefers exact match on name, then nip05 local part. */
export function findProfileByName(query: string): { pubkey: string; profile: any } | null {
  if (!query) return null;
  const q = query.toLowerCase();
  let best: { pubkey: string; profile: any } | null = null;

  for (const [pk, p] of profileCache.entries()) {
    if (!p) continue;
    const name = (p.name || '').toLowerCase();
    const displayName = (p.display_name || '').toLowerCase();
    const nip05 = (p.nip05 || '').toLowerCase();
    const nip05Local = nip05.split('@')[0] || '';

    // Exact match on name or display_name — return immediately
    if (name === q || displayName === q) return { pubkey: pk, profile: p };
    // Exact match on nip05 local part (e.g. "bob" matches "bob@example.com")
    if (nip05Local === q && !best) best = { pubkey: pk, profile: p };
  }
  return best;
}

export function fetchProfile(pubkey: string): Promise<any> {
  // Instant cache hit
  const cached = profileCache.get(pubkey);
  if (cached !== undefined) return Promise.resolve(cached);

  return new Promise((resolve) => {
    requestProfile(pubkey, (profile) => resolve(profile));
    // Safety timeout — don't hang forever
    setTimeout(() => resolve(null), 10000);
  });
}

export function useNostrProfile(pubkey: string | undefined) {
  const [profile, setProfile] = useState<any>(() => {
    if (!pubkey) return null;
    const cached = profileCache.get(pubkey);
    return cached !== undefined ? cached : null;
  });

  useEffect(() => {
    if (!pubkey) return;

    // Always register via requestProfile so we get both:
    // 1. Instant cache hit (callback fires immediately with cached data)
    // 2. Stale-while-revalidate updates (callback fires again with fresh data)
    let cancelled = false;
    requestProfile(pubkey, (p) => {
      if (!cancelled) setProfile(p);
    });

    return () => { cancelled = true; };
  }, [pubkey]);

  return { profile, loading: profile === null && !!pubkey };
}

// ══════════════════════════════════════════════════════════════════
//  BATCHED EVENT-BY-ID FETCH
//
//  Resolving referenced notes (embeds, thread roots, quotes) used to
//  open ONE relay subscription per note. A community feed with many
//  note-link posts opened dozens of simultaneous REQs; relays such as
//  nos.lol cap concurrent subscriptions per connection, so most were
//  dropped and only the first couple resolved — the rest fell back to
//  a "note" pill / "Post not found". This is the batched fix: all id
//  lookups in a short window collapse into a SINGLE `{ ids: [...] }`
//  REQ per relay, so there is no subscription storm.
//
//  A user's personal NIP-65 read list may also not include the relay
//  where a referenced note lives, so we always query a broad fallback
//  set in addition to the viewer's read relays.
// ══════════════════════════════════════════════════════════════════

const EVENT_LOOKUP_RELAYS = [
  'wss://relay.bchnostr.com',
  'wss://nos.lol',
  'wss://relay.primal.net',
  'wss://relay.nostr.net',
  'wss://relay.nostr.band',
  'wss://relay.snort.social',
  'wss://purplepag.es',
  'wss://nostr.wine',
];

const eventCache: Map<string, Event | null> = new Map();
const eventPending: Set<string> = new Set();
const eventCallbacks: Map<string, Set<(e: Event | null) => void>> = new Map();
let eventBatchTimer: any = null;
const eventBatchQueue: string[] = [];

function resolveEvent(id: string, event: Event | null) {
  // Keep the newest we've seen (edited posts can share an id space; ids are
  // unique per event so this simply records the first valid hit).
  if (event && eventCache.get(id)) return;
  eventCache.set(id, event);
  eventPending.delete(id);
  eventCallbacks.get(id)?.forEach(cb => cb(event));
  if (event) eventCallbacks.delete(id);
}

function flushEventBatch() {
  if (eventBatchQueue.length === 0) return;
  const ids = [...new Set(eventBatchQueue)];
  eventBatchQueue.length = 0;

  const subId = 'e' + (_nextId++);
  // Query the viewer's read relays PLUS the broad fallback set.
  const relays = [...new Set([...getReadRelays(), ...EVENT_LOOKUP_RELAYS])];

  subHandlers.set(subId, (data: any) => {
    if (data[0] === 'EVENT' && data[1] === subId) {
      const event = data[2] as Event;
      if (event?.id) resolveEvent(event.id, event);
    } else if (data[0] === 'EOSE' && data[1] === subId) {
      // Mark any ids we didn't get as null so we stop re-requesting, but
      // only after a short grace period to let slow relays deliver.
      setTimeout(() => {
        ids.forEach(id => {
          if (!eventCache.has(id)) resolveEvent(id, null);
        });
        closeSub(subId);
      }, 1500);
    }
  });

  // A single REQ with all ids — one subscription per relay, not per note.
  relays.forEach(url => sendReq(url, subId, [{ ids }]));

  // Hard safety timeout in case some relays never send EOSE.
  setTimeout(() => {
    ids.forEach(id => {
      if (!eventCache.has(id)) resolveEvent(id, null);
    });
    closeSub(subId);
  }, 8000);
}

function requestEvent(id: string, callback: (e: Event | null) => void) {
  const cached = eventCache.get(id);
  if (cached !== undefined) {
    callback(cached);
    if (cached !== null) return; // resolved hit — done
    // cached === null (previous miss): fall through to retry, since the
    // note may have propagated to relays since the last attempt.
  }

  if (!eventCallbacks.has(id)) eventCallbacks.set(id, new Set());
  eventCallbacks.get(id)!.add(callback);

  if (eventPending.has(id)) return;
  eventPending.add(id);
  eventBatchQueue.push(id);
  if (!eventBatchTimer) {
    // 30ms debounce collects the burst of embeds that mount together.
    eventBatchTimer = setTimeout(() => { eventBatchTimer = null; flushEventBatch(); }, 30);
  }
}

export function useNostrEvent(id: string | undefined, allRelays = false) {
  // `allRelays` retained for API compatibility. Resolution now always uses
  // the batched, broad-relay fetcher below, so referenced notes resolve
  // reliably regardless of how many embeds render at once.
  void allRelays;

  const [event, setEvent] = useState<Event | null>(() => (id ? eventCache.get(id) ?? null : null));
  const [loading, setLoading] = useState<boolean>(() => !!id && eventCache.get(id) === undefined);

  useEffect(() => {
    if (!id) { setEvent(null); setLoading(false); return; }
    // Also check locally-published events (not yet on relays).
    const local = getMatchingLocalEvents([{ ids: [id] }])[0];
    if (local) { setEvent(local); setLoading(false); return; }

    let cancelled = false;
    const cached = eventCache.get(id);
    if (cached !== undefined) setEvent(cached);
    setLoading(cached === undefined || cached === null);

    requestEvent(id, (e) => {
      if (cancelled) return;
      setEvent(e);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [id]);

  return { event, loading };
}

